import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercentage } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import ChatbotAi from "../components/ChatbotAi";
import '../css/calculatorpages.css';

const SICalculator = () => {
    const [investmentAmount, setInvestmentAmount] = useState('');
    const [rateOfReturn, setRateOfReturn] = useState('');
    const [investmentTenure, setInvestmentTenure] = useState('');
    const [amountInvested, setAmountInvested] = useState(null);
    const [totalReturn, setTotalReturn] = useState(null);
    const [maturityAmount, setMaturityAmount] = useState(null);
    const [isIpadPro, setIsIpadPro] = useState(false);

    const navigate = useNavigate();

    const handleBackClick = () => {
        navigate('/#financial-calculators-section');
    }

    useEffect(() => {
        const checkIpadPro = () => {
            const width = window.innerWidth;
            setIsIpadPro(width === 1024 || width === 1366);
        };

        checkIpadPro();
        window.addEventListener('resize', checkIpadPro);

        return () => {
            window.removeEventListener('resize', checkIpadPro);
        };
    }, []);

    const calculateSI = () => {
        const P = parseFloat(investmentAmount);
        const R = parseFloat(rateOfReturn);
        const T = parseFloat(investmentTenure);

        if (P >= 0 && R >= 0 && T >= 0) {
            const calculatedInterest = (P * R * T) / 100;
            const calculatedMaturityAmount = P + calculatedInterest;

            setAmountInvested(P.toFixed(2));
            setTotalReturn(calculatedInterest.toFixed(2));
            setMaturityAmount(calculatedMaturityAmount.toFixed(2));
        } else {
            setAmountInvested(null);
            setTotalReturn(null);
            setMaturityAmount(null);
        }
    };

    const ipadProLastInputStyle = isIpadPro ? {
        marginLeft: '0',
        marginRight: 'auto',
        alignSelf: 'flex-start'
    } : {};

    return (
        <div>
            <Helmet>
                <title>Simple Interest Calculator | Plan Your Earnings Clearly</title>
                <meta name="description" content="Calculate simple interest on your loans or investments with ease. Use our fast SI calculator to plan your finances effectively and precisely." />
                <link rel="canonical" href="https://policysamjho.com/calculators/si-calculator/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "Simple Interest Calculator",
                        "description": "Calculate simple interest on your loans or investments with ease. Use our fast SI calculator to plan your finances effectively and precisely.",
                        "url": "https://policysamjho.com/calculators/si-calculator/",
                        "applicationCategory": "FinancialApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "provider": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>

            <div className="calculator-page-container">
                <button onClick={handleBackClick} className='calculator-back-button'>
                    Back
                </button>
                <div className="calculator-content-wrapper">
                    <div className="calculator-info-section">
                        <div className="calculator-header-content">
                            <FontAwesomeIcon icon={faPercentage} className="calculator-header-icon" />
                            <h1 className="calculator-title">Simple Interest Calculator</h1>
                        </div>
                        <p className="calculator-description">
                            A simple interest calculator is a financial instrument used to calculate interest at a predetermined rate 
                            that is earned or payable on a principal amount over a certain period of time. This calculator lets 
                            users enter the principal amount, interest rate, and tenure and then instantly displays the amount of 
                            simple interest accrued and the final amount receivable or payable. The free online simple interest 
                            calculator provides instant and error-free results that streamline interest calculations. 
                            Read on to know how a simple interest calculator works, its calculation formula, how to use the 
                            calculator as well as the key differences between simple interest and compound interest.
                        </p>
                    </div>
                    <div className={`calculator-main-section ${maturityAmount === null ? 'no-result' : ''}`}>
                        <div className="calculator-input-section">
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="investmentAmount">Investment Amount (₹)</label>
                                    <input
                                        type="number"
                                        id="investmentAmount"
                                        value={investmentAmount}
                                        onChange={(e) => setInvestmentAmount(e.target.value)}
                                        placeholder="e.g. 100000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="rateOfReturn">Expected Rate of Return (%)</label>
                                    <input
                                        type="number"
                                        id="rateOfReturn"
                                        value={rateOfReturn}
                                        onChange={(e) => setRateOfReturn(e.target.value)}
                                        placeholder="e.g. 7"
                                    />
                                </div>
                            </div>
                            <div className="input-group" style={ipadProLastInputStyle}>
                                <label htmlFor="investmentTenure">Investment Tenure (Years)</label>
                                <input
                                    type="number"
                                    id="investmentTenure"
                                    value={investmentTenure}
                                    onChange={(e) => setInvestmentTenure(e.target.value)}
                                    placeholder="e.g. 3"
                                />
                            </div>
                            <button onClick={calculateSI} className="calculate-button">Calculate</button>
                        </div>

                        {maturityAmount !== null && (
                            <div className="calculator-result-section">
                                <h2>You Get</h2>
                                <div className="result-item">
                                    <span>Amount Invested</span>
                                    <span className="result-value">₹{amountInvested}</span>
                                </div>
                                <div className="result-item">
                                    <span>Total Return</span>
                                    <span className="result-value">₹{totalReturn}</span>
                                </div>
                                <div className="result-item">
                                    <span>Maturity amount</span>
                                    <span className="result-value">₹{maturityAmount}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ChatbotAi />
        </div>
    );
};

export default SICalculator;